import type { TaskItem } from '@routes/ProductPage/index.type';
import { memo, useCallback, useState } from 'react';
import type { MouseEventHandler, FC } from 'react';

import icons from '@icons';
import { Picture } from '@ui';
import { useAddTaskToProgressMutation } from '@queries/index';
import TaskModal from '@components/Modals/TaskModal';
import { twcls } from '@utils/twcls';

const {
  ruble: { black: rubleBlackFilled }
} = icons.Fill;
const {
  checkmark: { green: checkmarkGreenOutlined },
  chevronRight: { black: chevronRightBlackOutlined }
} = icons.Outline;
interface ProductTaskProps {
  task: TaskItem;
  isMobile?: boolean;
}

const ProductTask: FC<ProductTaskProps> = ({ task, isMobile }) => {
  const [currentTask, setCurrentTask] = useState<TaskItem>(task);
  const [isTaskModalOpened, setIsTaskModalOpened] = useState(false);

  const openTaskModal: MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsTaskModalOpened(true);
  };

  const closeTaskModal = (): void => {
    setIsTaskModalOpened(false);
  };

  const addTaskMutation = useAddTaskToProgressMutation();
  const { mutateAsync: addTaskToProgress } = addTaskMutation;

  const activateTask = useCallback(async (): Promise<void> => {
    if (!currentTask) {
      return;
    }
    await addTaskToProgress(Number(currentTask.id))
      .then(() => {
        const newTask: NonNullable<TaskItem> = {
          ...currentTask,
          type: 'progress'
        };
        setCurrentTask(newTask);
      })
      .catch(e => {
        console.log('Error: ', e);
      });
  }, [addTaskToProgress, currentTask]);

  if (!currentTask) {
    return null;
  }
  return (
    <>
      {isTaskModalOpened && (
        <TaskModal
          activate={activateTask}
          task={currentTask}
          onClose={closeTaskModal}
        />
      )}
      {isMobile ? (
        <div
          onClick={openTaskModal}
          className={twcls(
            'flex items-center rounded-full p-1 pr-1.5 text-xs bg-[var(--mid-yellow)]',
            currentTask.type === 'progress' && 'bg-[var(--light-green)]'
          )}
        >
          <Picture
            icon={rubleBlackFilled}
            height={16}
            width={16}
            pictureClassName={'mr-0.5'}
          />
          <div className=''>
            +{currentTask.amount}₽ за{' '}
            <span className='text-xs underline bg-transparent'>задание</span>
          </div>
        </div>
      ) : (
        <div
          onClick={openTaskModal}
          className={twcls(
            'cursor-pointer h-12 bg-[var(--mid-yellow)] rounded-xl px-2 w-full grid grid-cols-[auto,116px] gap-x-4 items-center',
            currentTask.type === 'progress' && 'bg-[var(--light-green)]'
          )}
        >
          <div className=''>
            {task?.amount && (
              <div className='mb-1 font-medium '>
                +{task.amount}₽ при выполнении задания
              </div>
            )}
            <div className='flex items-center text-xs'>
              Подробнее
              <Picture
                icon={chevronRightBlackOutlined}
                width={16}
                height={16}
              />
            </div>
          </div>
          {currentTask.type === 'progress' ? (
            <div className='flex items-center w-full px-3 py-2 text-xs bg-white rounded-lg text-[var(--main-green)]'>
              Активировано
              <Picture
                icon={checkmarkGreenOutlined}
                height={16}
                width={16}
                pictureClassName={'ml-0.5'}
              />
            </div>
          ) : (
            <button
              type='button'
              onClick={async e => {
                e.stopPropagation();
                await activateTask();
              }}
              className='w-full px-3 py-2 text-sm bg-white rounded-lg duration-200 cursor-pointer transition-colors hover:bg-[var(--dark-red)] hover:text-white'
            >
              Активировать
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default memo(ProductTask);
